// Flat cartoon's cast stage (spec G14-flat-cartoon, PR d; no live equivalent, Flat cartoon-6): the
// draft's eight stills, the two character sheets wide and the six start stills 9:16, each with its
// Regenerate, and Start over. As Pixar Talking Ad's (`PixarCast.tsx`).
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { FlatCartoonDraftStill, FlatCartoonStillName } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { StillCard } from "./StillCard";
import { STILL_ORDER, stillWaiting } from "./flat-cartoon-draft";
import classes from "./podcast.module.css";

/** Each still's label: the playbook's names (L3659-3700). */
export const STILL_LABELS: Readonly<Record<FlatCartoonStillName, string>> = {
  host_sheet: "Host",
  caller_sheet: "Caller",
  studio: "Studio",
  car: "Car",
  metaphor: "Metaphor",
  prescribe: "Prescription",
  closeup: "Close-up",
  hero: "Product hero",
};
const PENDING = { status: "pending", url: null, problem: null } as const;

function stillItem(still: FlatCartoonDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface FlatCartoonCastProps {
  stills: readonly FlatCartoonDraftStill[];
  busy: boolean;
  resetting: boolean;
  onRegenerate: (name: FlatCartoonStillName) => void;
  onStartOver: () => void;
}

/** "Cast & set" and the eight stills, each with its label; a done still opens in the lightbox. */
export function FlatCartoonCast({ stills, busy, resetting, onRegenerate, onStartOver }: FlatCartoonCastProps) {
  const [preview, setPreview] = useState<FlatCartoonStillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        <Button variant="subtle" size="compact-sm" disabled={resetting || busy} onClick={() => setConfirming(true)}>
          Start over
        </Button>
      </Group>
      <div className={classes.stills}>
        {STILL_ORDER.map((name) => (
          <StillCard
            key={name}
            label={STILL_LABELS[name]}
            still={stills.find((each) => each.name === name) ?? PENDING}
            waiting={stillWaiting(name, stills)}
            busy={busy}
            wide={name.endsWith("_sheet")}
            onRegenerate={() => onRegenerate(name)}
            onFixLabel={null}
            onPreview={() => setPreview(name)}
          />
        ))}
      </div>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as FlatCartoonStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script and the eight images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </Stack>
  );
}
